import React, {Component} from "react";
import {Control, Field, Icon, Label, Select, TextArea} from "bloomer";
import Card from "../../../elements/Card";
import Button from "../../../elements/Button";
import SendGAEvent, {GAEventActions, GAEventModels} from "../../../utils/SendGAEvent";
import ErrorUtils from "../../../utils/ErrorUtils";
import {toast} from "react-toastify";
import SectionName from "../../../elements/SectionName";
import InformationCard from "../../../elements/InformationCard";
import Spacer from "../../../elements/Spacer";
import InputWarning from "../../../elements/InputWarning";
import ModelContentContainer from "../../../elements/ModelContentContainer";
import MemeImages from "../../../components/model/MemeGenerator/MemeImages";
import MemeFonts from "../../../components/model/MemeGenerator/MemeFonts";
import {getGeneratedMeme} from "../../../api/models/MemeGenerator";
import {INVALID_FIELD_STYLE} from "../../../utils/Constants";

interface IMemeGeneratorModelState {
    downloading: boolean;
    loadingLibs: boolean;
    loading: boolean;
    imageOptions: any;
    selectedImage: string;
    emptyImage: boolean;
    topText: string;
    emptyTopText: boolean;
    bottomText: string;
    emptyBottomText: boolean;
    fontOptions: any;
    selectedFont: string;
    emptyFont: boolean;
    result: any;
    errorMessage: string;
}

class MemeGeneratorModel extends Component<{}, IMemeGeneratorModelState> {
    public EMPTY_VALUE = "-";

    public constructor(props: {}) {
        super(props);

        this.state = {
            downloading: false,
            loadingLibs: true,
            loading: false,
            imageOptions: [],
            selectedImage: this.EMPTY_VALUE,
            emptyImage: false,
            topText: "",
            emptyTopText: false,
            bottomText: "",
            emptyBottomText: false,
            fontOptions: [],
            selectedFont: this.EMPTY_VALUE,
            emptyFont: false,
            result: "",
            errorMessage: ""
        };
    }

    public async componentDidMount() {
        const imageOptions = await MemeImages.list();
        if (imageOptions) {
            this.setState({
                imageOptions
            });
        }
        const fontOptions = await MemeFonts.list();
        if (fontOptions) {
            this.setState({
                fontOptions
            });
        }
        this.setState({loadingLibs: false});
        SendGAEvent(GAEventActions.VIEW_MODEL, GAEventModels.MEME_GENERATOR);
    }

    public handleImageChange = (e: any) => {
        const value = e.target.value;
        this.setState({
            selectedImage: value,
            emptyImage: value === this.EMPTY_VALUE,
            result: ""
        });
    };

    public handleTopTextChange = (e: any) => {
        const value = e.target.value;
        this.setState({
            topText: value,
            emptyTopText: !value.trim()
        });
    };

    public handleBottomTextChange = (e: any) => {
        const value = e.target.value;
        this.setState({
            bottomText: value,
            emptyBottomText: !value.trim()
        });
    };

    public handleFontChange = (e: any) => {
        const value = e.target.value;
        this.setState({
            selectedFont: value,
            emptyFont: value === this.EMPTY_VALUE
        });
    };


    public onSubmit = async (e: any) => {
        e.target.blur();

        let valid = true;

        if (this.state.loading) {
            return;
        }

        if (this.state.emptyImage || this.state.emptyFont || this.state.emptyTopText || this.state.emptyBottomText) {
            valid = false;
        }

        if (!this.state.emptyImage && this.state.selectedImage === this.EMPTY_VALUE) {
            this.setState({
                emptyImage: true
            });
            valid = false;
        }

        if (!this.state.emptyTopText && !this.state.topText.trim()) {
            this.setState({
                emptyTopText: true
            });
            valid = false;
        }

        if (!this.state.emptyBottomText && !this.state.bottomText.trim()) {
            this.setState({
                emptyBottomText: true
            });
            valid = false;
        }

        if (!this.state.emptyFont && this.state.selectedFont === this.EMPTY_VALUE) {
            this.setState({
                emptyFont: true
            });
            valid = false;
        }

        if (!valid) {
            this.setState({result: ""});
            return;
        }

        this.setState({
            loading: true,
            result: "",
            errorMessage: ""
        });

        let errorOccurred = false;

        const formData = {
            image: this.state.selectedImage,
            topText: this.state.topText.trim(),
            bottomText: this.state.bottomText.trim(),
            font: this.state.selectedFont,
            fontSize: 50
        };

        const response = await getGeneratedMeme(formData).catch((error) => {
            if (ErrorUtils.requestLimitExceeded(error.response)) {
                this.setState({loading: false});
                toast.error(ErrorUtils.messageContent.REQUEST_LIMIT_EXCEEDED);
            } else {
                this.setState({
                    loading: false,
                    errorMessage: "Couldn't generate image."
                });
            }
            errorOccurred = true;
        });

        if (errorOccurred) {
            this.setState({loading: false});
            return;
        }

        if (response && response.data) {
            const blob = new Blob([response.data], {type: 'image/jpeg'});
            this.setState({
                loading: false,
                result: URL.createObjectURL(blob)
            });
        } else {
            this.setState({
                loading: false,
                errorMessage: "Couldn't process image."
            });
        }

        SendGAEvent(GAEventActions.CALL_MODEL, GAEventModels.MEME_GENERATOR);
    };

    public resetState = async (e: any) => {
        e.target.blur();

        this.setState({
            selectedImage: this.EMPTY_VALUE,
            emptyImage: false,
            topText: "",
            emptyTopText: false,
            bottomText: "",
            emptyBottomText: false,
            selectedFont: this.EMPTY_VALUE,
            emptyFont: false,
            result: "",
            errorMessage: ""
        });
    };

    public downloadImage = (e: any) => {
        e.target.blur();

        if (!this.state.loading && this.state.result) {
            this.setState({downloading: true}, () => {
                setTimeout(() => {
                    const element = document.createElement("a");
                    element.href = this.state.result;
                    element.download = `${this.state.selectedImage}-${new Date().getTime()}.jpg`;
                    document.body.appendChild(element);
                    element.click();
                    document.body.removeChild(element);

                    this.setState({downloading: false});
                }, 1000);
            });
        }
    };

    public render() {
        if (this.state.loadingLibs) {
            return <ModelContentContainer>
                <Card>
                    <div style={{textAlign: 'center'}}>
                        <Spacer/>
                        <span>Loading...</span>
                        <Icon className="fas fa-spinner fa-spin"/>
                    </div>
                    <Spacer/>
                </Card>
            </ModelContentContainer>;
        }

        return <ModelContentContainer>
                    <Card>
                        <Field>
                            <Label>Select Image:</Label>
                            <Control>
                                <Select
                                    value={this.state.selectedImage}
                                    onChange={this.handleImageChange}
                                >
                                    {
                                        this.state.imageOptions.map((imageName: any) => {
                                            return <option key={imageName} value={imageName}>{imageName}</option>;
                                        })
                                    }
                                </Select>
                                {
                                    this.state.emptyImage ? <div>
                                        <InputWarning>Please select image.</InputWarning>
                                    </div> : <></>
                                }
                            </Control>
                        </Field>
                        <Spacer/>
                        <Field>
                            <Control>
                                <SectionName>Top Text:</SectionName>
                                <TextArea
                                    className="has-fixed-size"
                                    style={this.state.emptyTopText ? INVALID_FIELD_STYLE : undefined}
                                    onChange={this.handleTopTextChange}
                                    onPaste={this.handleTopTextChange}
                                    value={this.state.topText}
                                    placeholder={"Enter top text"}
                                    rows={2}
                                />
                                {
                                    this.state.emptyTopText ? <div>
                                        <InputWarning>Please input text.</InputWarning>
                                    </div> : <></>
                                }
                            </Control>
                        </Field>
                        <Spacer/>
                        <Field>
                            <Control>
                                <SectionName>Bottom Text:</SectionName>
                                <TextArea
                                    style={(this.state.emptyBottomText) ? INVALID_FIELD_STYLE : undefined}
                                    className="has-fixed-size"
                                    onChange={this.handleBottomTextChange}
                                    onPaste={this.handleBottomTextChange}
                                    value={this.state.bottomText}
                                    placeholder={"Enter bottom text"}
                                    rows={2}
                                />
                                {
                                    this.state.emptyBottomText ? <div>
                                        <InputWarning>Please input text.</InputWarning>
                                    </div> : <></>
                                }
                            </Control>
                        </Field>
                        <Spacer/>
                        <Field>
                            <Label>Select Font:</Label>
                            <Control>
                                <Select
                                    value={this.state.selectedFont}
                                    onChange={this.handleFontChange}
                                >
                                    {
                                        this.state.fontOptions.map((fontName: any) => {
                                            return <option key={fontName} value={fontName}>{fontName}</option>;
                                        })
                                    }
                                </Select>
                                {
                                    this.state.emptyFont ? <div>
                                        <InputWarning>Please select font.</InputWarning>
                                    </div> : <></>
                                }
                            </Control>
                        </Field>
                        <Spacer/>
                        <div>
                            <Button onClick={this.onSubmit}>Submit</Button>
                            <Button onClick={this.resetState} style={{marginLeft: "5px"}}>Reset</Button>
                        </div>
                        {
                            this.state.loading && <div style={{textAlign: 'center'}}>
                                <Spacer/>
                                <span>Loading...</span>
                                <Icon className="fas fa-spinner fa-spin"/>
                            </div>
                        }
                        <Spacer/>
                        {
                            !this.state.loading && this.state.result ? <div>
                                <SectionName>Output:</SectionName>
                                <InformationCard>
                                    <div style={{
                                        margin: '20px',
                                        textAlign: 'center',
                                    }}>
                                        <img
                                            alt="Meme"
                                            src={this.state.result}
                                        />
                                    </div>
                                </InformationCard>
                                <Spacer/>
                                <div style={{textAlign: 'center'}}>
                                    {
                                        this.state.downloading ? <div>
                                            <span>Downloading...</span>
                                            <Icon className="fas fa-spinner fa-spin"/>
                                        </div> : <Button
                                            title="Download Image"
                                            style={{marginRight: "5px"}}
                                            onClick={(e: any) => {
                                                this.downloadImage(e);
                                            }}
                                        >
                                            <Icon className="fas fa-download" style={{marginRight: '5px'}}/>Download
                                        </Button>
                                    }
                                </div>
                                <Spacer/>
                            </div> : <></>
                        }
                        {
                            !this.state.loading && this.state.errorMessage ? <div>
                                {this.state.errorMessage}
                            </div> : <></>
                        }
                    </Card>
                </ModelContentContainer>;
    }

}

export default MemeGeneratorModel;


// WEBPACK FOOTER //
// ./src/routes/Dashboard/MemeGenerator/MemeGeneratorModel.tsx